import React from 'react';
import Input from '../shared/Input/Input';
import type { RouteStop, RouteFare } from '../../types/api';

interface Props {
  stops: RouteStop[];
  fares: RouteFare[];
  onChange: (fares: RouteFare[]) => void;
  disabled?: boolean;
}

const RouteFareMatrix: React.FC<Props> = ({ stops, fares, onChange, disabled }) => {
  const ordered = [...stops].sort((a, b) => a.sequence - b.sequence);
  const matrix = new Map<string, string>();
  for (const f of fares) {
    matrix.set(`${f.boarding_stop_id}-${f.alighting_stop_id}`, f.amount);
  }

  const set = (bId: number, aId: number, amount: string) => {
    const key = `${bId}-${aId}`;
    const next = fares.filter(f => `${f.boarding_stop_id}-${f.alighting_stop_id}` !== key);
    if (amount) {
      next.push({
        id: 0, boarding_stop_id: bId, alighting_stop_id: aId, amount,
      });
    }
    onChange(next);
  };

  if (ordered.length < 2) {
    return <p className="text-sm text-slate-500">Add at least 2 stops to configure fares.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="text-sm">
        <thead>
          <tr>
            <th className="px-2 py-2 text-xs uppercase text-slate-500">Boarding ↓ / Alighting →</th>
            {ordered.map(s => (
              <th key={s.stop_id} className="px-2 py-2 text-xs uppercase text-slate-500">{s.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ordered.map(b => (
            <tr key={b.stop_id}>
              <th className="px-2 py-2 text-left text-xs font-semibold text-slate-700">{b.name}</th>
              {ordered.map(a => {
                if (a.sequence <= b.sequence) {
                  return <td key={a.stop_id} className="px-2 py-2 bg-slate-100" />;
                }
                const key = `${b.stop_id}-${a.stop_id}`;
                return (
                  <td key={a.stop_id} className="px-1 py-1">
                    <Input
                      type="number"
                      placeholder="KES"
                      value={matrix.get(key) ?? ''}
                      disabled={disabled}
                      onChange={(e) => set(b.stop_id, a.stop_id, e.target.value)}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RouteFareMatrix;
